/*
 * The sky is rolled, not told. Every full page load picks a time of day and a
 * landscape at random (or takes them from ?phase= and ?scene= for that load),
 * stamps them on <html>, and never stores anything.
 *
 * Everything in this file is a self-contained ES5 function: no closures over
 * module scope, no spread, classes or async. BOOT_SCRIPT (at the bottom) is
 * built from these functions' source text and inlined into <head> by
 * gatsby-ssr.js, so the palette and landscape are on <html> before first
 * paint. The React store (useSky) calls the same functions afterwards.
 */

export var PHASES = ['dawn', 'day', 'golden', 'dusk', 'night']
export var SCENES = ['bay', 'desert', 'forest']

export function nextPhase(phase) {
  var ORDER = ['dawn', 'day', 'golden', 'dusk', 'night']
  var i = ORDER.indexOf(phase)
  return ORDER[(i + 1) % ORDER.length]
}

/** A time of day named in the URL (`?phase=dusk`), for this load only; else null. */
export function readPhase(search) {
  var VALID = ['dawn', 'day', 'golden', 'dusk', 'night']
  var m = /[?&]phase=([a-z]+)/.exec(search || '')
  return m && VALID.indexOf(m[1]) !== -1 ? m[1] : null
}

/** A position in the day cycle named in the URL (`?t=0.3`), for QA; else null. */
export function readT(search) {
  var m = /[?&]t=([0-9.]+)/.exec(search || '')
  var t = m ? parseFloat(m[1]) : NaN
  return !isNaN(t) && t >= 0 && t < 1 ? t : null
}

/** A landscape named in the URL (`?scene=bay`), for this load only; else null. */
export function readScene(search) {
  var VALID = ['bay', 'desert', 'forest']
  var m = /[?&]scene=([a-z]+)/.exec(search || '')
  return m && VALID.indexOf(m[1]) !== -1 ? m[1] : null
}

/**
 * One day and night as a single cycle, t from 0 to 1: the sun climbs from
 * just under the horizon, peaks at 0.3 and sets at 0.6, and the moon does
 * the same over the rest, so the two hand over out of sight. The phases are
 * windows on that cycle, cut where the body's height crosses the mark that
 * suits them (day from 0.3 rising, golden under 0.4 setting, dusk under
 * 0.1). Heights run from -0.1 to 1 at three decimals, with the stars up at
 * night and half up at dusk.
 */
export function dayAt(t) {
  var STARTS = [['dawn', 0], ['day', 0.071], ['golden', 0.51], ['dusk', 0.566], ['night', 0.6]]
  t = t - Math.floor(t)
  var alt = t < 0.6
    ? 1.1 * Math.sin(Math.PI * t / 0.6) - 0.1
    : 1.1 * Math.sin(Math.PI * (t - 0.6) / 0.4) - 0.1
  var phase = 'dawn'
  for (var i = 0; i < STARTS.length; i++) if (t >= STARTS[i][1]) phase = STARTS[i][0]
  return {
    phase: phase,
    sunAlt: Math.round(alt * 1000) / 1000,
    stars: phase === 'night' ? 1 : phase === 'dusk' ? 0.4 : 0,
    t: t,
  }
}

/** Where a jump into a phase lands: a little way in, with its body just up. */
export function enterT(phase) {
  var ENTER = { dawn: 0.04, day: 0.1, golden: 0.52, dusk: 0.567, night: 0.66 }
  return ENTER[phase] !== undefined ? ENTER[phase] : ENTER.golden
}

/**
 * A sky for a phase: a random moment inside its window, with the sun (or
 * moon) showing unless the phase is dusk, which is allowed to set.
 */
export function skyFor(phase, dayAt) {
  var WINDOW = { dawn: [0.04, 0.071], day: [0.1, 0.51], golden: [0.52, 0.566], dusk: [0.567, 0.6], night: [0.66, 1] }
  var w = WINDOW[phase] || WINDOW.golden
  var day
  for (var i = 0; i < 24; i++) {
    // Rounded to the stamp's four decimals, and held short of the window's
    // end so the rounding can never tip it into the next phase.
    day = dayAt(Math.min(Math.round((w[0] + Math.random() * (w[1] - w[0])) * 10000) / 10000, w[1] - 0.0001))
    if (phase === 'dusk' || day.sunAlt >= 0.12) break
  }
  return day
}

/**
 * The moment in the day for this page load: the URL's if it names one (a
 * phase, or a cycle position), otherwise a random one with the sun or moon
 * well up. Rolled once and stamped on <html> as data-day-t (plus the phase
 * and height it implies); the clock moves that stamp on, and every later
 * read takes the stamp, so a refresh never re-rolls. The server's default
 * stamp is not a roll, hence the data-rolled marker.
 */
export function choosePhase(search, root, readPhase, readT, skyFor, dayAt) {
  var named = readPhase(search)
  var namedT = readT(search)
  var stamped = root.getAttribute('data-phase')
  var rolled = root.getAttribute('data-rolled') === '1'
  var stampedT = parseFloat(root.getAttribute('data-day-t'))
  var day
  if (rolled && !isNaN(stampedT) && (namedT !== null || !named || stamped === named)) {
    day = dayAt(stampedT)
  } else if (namedT !== null) {
    day = dayAt(Math.round(namedT * 10000) / 10000)
  } else if (named) {
    day = skyFor(named, dayAt)
  } else {
    // A first roll always shows the sun (or the moon) well up: never dusk.
    for (var i = 0; i < 40; i++) {
      day = dayAt(Math.round(Math.random() * 10000) / 10000)
      if (day.phase !== 'dusk' && day.sunAlt >= 0.2) break
    }
  }
  root.setAttribute('data-phase', day.phase)
  root.setAttribute('data-sun-alt', day.sunAlt.toFixed(3))
  root.setAttribute('data-day-t', day.t.toFixed(4))
  root.setAttribute('data-rolled', '1')
  return day
}

/** The landscape for this page load: the URL's if it names one, else random, rolled once. */
export function chooseScene(search, root, readScene) {
  var VALID = ['bay', 'desert', 'forest']
  var scene = readScene(search)
  if (!scene) {
    var stamped = root.getAttribute('data-scene')
    scene = stamped && VALID.indexOf(stamped) !== -1
      ? stamped
      : VALID[Math.floor(Math.random() * VALID.length)]
  }
  root.setAttribute('data-scene', scene)
  return scene
}

/**
 * Scatters the three cloud banks once per page load, as CSS variables on
 * <html>, so no two loads frame up quite the same and the positions are in
 * place before first paint. Kept within bounds that hold them in the sky.
 */
export function scatterClouds(root) {
  if (root.getAttribute('data-clouds') === '1') return
  var names = ['--cloud-a', '--cloud-b', '--cloud-c']
  for (var i = 0; i < names.length; i++) {
    var dx = (Math.random() * 2 - 1) * 190
    var dy = (Math.random() * 2 - 1) * 68
    root.style.setProperty(names[i], dx.toFixed(1) + 'px ' + dy.toFixed(1) + 'px')
  }
  root.setAttribute('data-clouds', '1')
}

/** Writes the sky onto <html>, outside the React root, so hydration never sees it. */
export function applySky(doc, sky) {
  var SKY0 = { dawn: '#0f3358', day: '#163f6f', golden: '#24284f', dusk: '#0f1233', night: '#05070f' }
  var html = doc.documentElement
  html.setAttribute('data-phase', sky.phase)
  html.style.setProperty('--sun-alt', sky.sunAlt.toFixed(3))
  // The landscape's sun-lit parts follow the slow twin, stepped by the clock.
  html.style.setProperty('--sun-alt-slow', sky.sunAlt.toFixed(3))
  html.style.setProperty('--stars', String(sky.stars))
  var meta = doc.querySelector ? doc.querySelector('meta[name="theme-color"]') : null
  if (meta) meta.content = SKY0[sky.phase] || SKY0.golden
}

function boot(readPhase, readT, readScene, dayAt, skyFor, choosePhase, chooseScene, scatterClouds, applySky) {
  try {
    var root = document.documentElement
    var sky = choosePhase(location.search, root, readPhase, readT, skyFor, dayAt)
    chooseScene(location.search, root, readScene)
    scatterClouds(root)
    applySky(document, sky)
  } catch (e) { /* the CSS defaults (golden hour, the bay) stand */ }
}

export var BOOT_SCRIPT = '(' + boot.toString() + ')('
  + readPhase.toString() + ','
  + readT.toString() + ','
  + readScene.toString() + ','
  + dayAt.toString() + ','
  + skyFor.toString() + ','
  + choosePhase.toString() + ','
  + chooseScene.toString() + ','
  + scatterClouds.toString() + ','
  + applySky.toString() + ')'
